import React, { useState, useRef } from 'react';
import { MdConfirmationNumber } from 'react-icons/md';
import { Formik, FormikProps } from 'formik';

import { UserMe } from 'legacy/user/types';
import AlertModal from 'legacy/common/components/modal/AlertModal';
import { ButtonType } from 'legacy/common/components/button/Button';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import { showError } from 'legacy/common/utils/toast';

import MemberOnlyAccess from './components/MemberOnlyAccess';
import MainForm from './components/MainForm';
import { GoToStepFn, StepsId } from './types';
import computeInitialValues, {
  FormValueDto
} from './helpers/computeInitialValues';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';
import {
  OutgoingEventProductDto,
  EventProductType
} from 'api/dtos/events/products';
import useUnsavedChanges from './hooks/useUnsavedChanges';
import useProductTypeChange from './hooks/useProductTypeChange';
import { getValidationSchema } from './validations';

type TierFormProps = {
  isTierPrivate: boolean;
  onCancel: () => void;
  ticket?: OutgoingEventProductDto;
  onRemove?: () => void;
  user: UserMe;
  onSubmit: (values: OutgoingEventProductDto) => void;
  isDraft?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  lastUpdated?: string;
  moveToSection?: (sectionId: string) => void;
  formTitle?: string;
};

const pageTexts = {
  publicTier: {
    newTitle: 'New public tier',
    editTitle: 'Edit public tier'
  },
  privateTier: {
    newTitle: 'New private tier',
    editTitle: 'Edit private tier'
  }
};

const STEPS = {
  [StepsId.Main]: {
    icon: <MdConfirmationNumber size={20} />,
    onBack: null,
    title: (texts, isEditing) => (isEditing ? texts.editTitle : texts.newTitle),
    Component: MainForm,
    autoHeight: true
  },
  [StepsId.MemberOnlyAccess]: {
    icon: null,
    onBack: (goToStep) => () => goToStep(StepsId.Main),
    title: () => 'Member-Only Access',
    Component: MemberOnlyAccess,
    autoHeight: false
  }
};

const TicketForm = ({
  onCancel,
  ticket,
  isTierPrivate,
  onSubmit,
  onRemove,
  isDraft = false,
  isEditing = false,
  isLoading = false,
  formTitle,
  ...props
}: TierFormProps) => {
  const { event } = useEventEditorContext();
  const isEditingTicket = !!ticket;
  const texts = pageTexts[isTierPrivate ? 'privateTier' : 'publicTier'];
  const [currentStep, setCurrentStep] = useState(StepsId.Main);
  const [isDirty, setIsDirty] = useState(false);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const formikRef = useRef<FormikProps<FormValueDto>>(null);

  // Initial product type
  const initialType = ticket?.type || 'admission';

  // Custom hooks
  const {
    showUnsavedChangesAlert,
    handleCancel,
    handleDiscardChanges,
    closeAlert
  } = useUnsavedChanges(isDirty, onCancel);

  // Hook for handling product type changes
  useProductTypeChange(formikRef, ticket, isTierPrivate);

  // Dynamic validation based on current form values
  const validateForm = (values: FormValueDto) => {
    const schema = getValidationSchema(values.type as EventProductType, ticket);
    try {
      schema.validateSync(values, { abortEarly: false });
      return {};
    } catch (err) {
      return err.inner.reduce(
        (errors, error) => ({
          ...errors,
          [error.path]: error.message
        }),
        {}
      );
    }
  };

  const formData = STEPS[currentStep];

  const handleFormSubmit = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handleRemoveClick = () => {
    if (ticket && !ticket.canDelete) {
      showError({
        title: 'Cannot Remove Tier',
        message:
          'This tier cannot be deleted because tickets have already been purchased.'
      });
      return;
    }

    setIsRemoveLoading(true);
    if (onRemove) {
      onRemove();
    }
  };

  const onFormSubmit = (values: FormValueDto) => {
    setIsSubmitLoading(true);
    const newValues: any = { ...values };
    // Clean up temporary fields
    delete newValues?.startDateTime;
    delete newValues?.endDateTime;

    // Filter out empty questions
    if (newValues.purchaseRequirements) {
      const questions =
        newValues.purchaseRequirements.questions?.filter(
          ({ question }) => question !== ''
        ) || [];
      newValues.purchaseRequirements.questions = questions;
    }

    // Validate bundle components
    if (newValues.type === 'bundle') {
      const hasInvalidComponents = newValues.components.some(
        (comp) => !comp.productId
      );
      if (hasInvalidComponents) {
        setIsSubmitLoading(false);
        return;
      }
    }

    onSubmit(newValues as OutgoingEventProductDto);
  };

  const goToStep: GoToStepFn = (stepId) => {
    setCurrentStep(stepId);
  };

  const getPageTitle = () => {
    if (formTitle) {
      return formTitle;
    }

    if (currentStep === StepsId.MemberOnlyAccess) {
      return 'Member-Only Access';
    }
    return isEditingTicket ? texts.editTitle : texts.newTitle;
  };

  const handleStepBack = () => {
    if (currentStep === StepsId.MemberOnlyAccess) {
      goToStep(StepsId.Main);
    } else {
      handleCancel();
    }
  };

  return (
    <>
      <Header
        isDraft={isDraft}
        isEditing={isEditing}
        title={getPageTitle()}
        isLoading={isLoading}
        onBack={handleStepBack}
        description={event?.title}
      />
      <div className="pt-[50px] md:pt-[180px] -mt-[90px] pb-20 max-w-[630px] md:mx-auto">
        <div className="px-4">
          <Formik
            initialValues={computeInitialValues(
              ticket,
              isTierPrivate,
              initialType
            )}
            onSubmit={onFormSubmit}
            validate={validateForm}
            innerRef={formikRef}
          >
            {() => (
              <formData.Component
                goToStep={goToStep}
                ticket={ticket}
                isEditing={isEditingTicket}
                isTierPrivate={isTierPrivate}
                setIsDirty={setIsDirty}
                handleSubmit={handleFormSubmit}
                handleRemoveClick={handleRemoveClick}
                isRemoveLoading={isRemoveLoading}
                isSubmitLoading={isSubmitLoading}
                /* eslint-disable react/jsx-props-no-spreading  */
                {...props}
              />
            )}
          </Formik>
        </div>
      </div>

      {showUnsavedChangesAlert && (
        <AlertModal
          title="Unsaved Changes"
          message="You have unsaved changes that will be lost."
          submitLabel="Discard"
          submitButtonType={ButtonType.Danger}
          onSubmit={handleDiscardChanges}
          onCancel={closeAlert}
        />
      )}
    </>
  );
};

export default TicketForm;
